import React, { useEffect, useMemo, useState } from "react"
import WizardStep from "../WizardStep"
import { WizardState } from "../use-wizard"
import { useTranslation } from "react-i18next"
import { SearchBar } from "react-native-elements"
import MainScrollPanel from "../../../components/layout/MainScrollPanel"
import Panel from "../../../components/layout/Panel"
import { useDebouncedState } from "pusatec-react-utility"
import { useSelectUserGroupsQuery, useSelectUserQuery } from "./queries.generated"
import { theme } from "../../../theme"
import { ErrorMessage, Text, TouchButton } from "pusatec-react-native"
import { BackIcon } from "../../../components/BackIcon"
import { useLockersystemFeatures, useMyLockersystemPerm } from "../../../components/featureParameters"
import { TextInput, View, Switch } from "react-native"
import Base from "../../../components/layout/Base"

const isDefined = <T,>(value: T | null | undefined): value is T => value != null

export interface UserSelection {
	id?: string | null
	name?: string | null
	email?: string | null
	identityCode?: string | null
	groupId?: string | null
}

interface Props {
	wizardState: WizardState
	systemId: string
	maxSelectedUsers?: number
	user?: UserSelection | null
	onComplete(users: UserSelection[]): void
	onCancel(): void
	emailEnabled: boolean
	allowEmailWhenUserNotFound?: boolean
	identityCodeEnabled?: boolean
	allowGroupSelection?: boolean
}

export default function SelectUserStep(props: Props) {
	const { systemId, user, onComplete, onCancel, wizardState, identityCodeEnabled = false, allowGroupSelection = false } = props
	const { t } = useTranslation()

	const [searchString, debouncedSearchString, setSearchString] = useDebouncedState(
		user ? (user.email || user.name) : "",
		300
	)
	const searchStringIncomplete = !searchString || searchString.length < 3
	const [identityCode, setIdentityCode] = useState("")
	const [useIdentityCode, setUseIdentityCode] = useState(false)

	const perm = useMyLockersystemPerm(systemId)
	const lockersystem = useLockersystemFeatures({ systemId: systemId! })
	const deliveryWithIdentityCode = (lockersystem?.deliveryWithIdentityCode ?? false) && identityCodeEnabled
	const accessDenied = perm?.accessDenied === true
	const isAdmin = !!perm?.isAdmin && !accessDenied
	const groupSelectionEnabled = allowGroupSelection && isAdmin

	const [selectedUsers, setSelectedUsers] = React.useState<UserSelection[]>([])
	const [pendingUsers, setPendingUsers] = React.useState<UserSelection[]>([])

	useEffect(() => {
		if (deliveryWithIdentityCode) {
			setUseIdentityCode(true)
		} else {
			setUseIdentityCode(false)
			setIdentityCode("")
			setSelectedUsers(users => users.filter(u => !u.identityCode))
			setPendingUsers(users => users.filter(u => !u.identityCode))
		}
	}, [deliveryWithIdentityCode])

	const { data, loading, error, refetch } = useSelectUserQuery({
		skip: !isAdmin || searchStringIncomplete || accessDenied,
		variables: {
			input: {
				accessToLockersystemId: systemId,
				searchWord: debouncedSearchString,
			},
		},
	})

	const { data: groupsData, loading: groupsLoading, error: groupsError, refetch: refetchGroups } = useSelectUserGroupsQuery({
		skip: !groupSelectionEnabled,
		variables: {
			lockersystemId: systemId
		}
	})

	const users = useMemo(() => {
		if (!isAdmin) return undefined
		return data?.users?.users?.
			filter(user => !selectedUsers.some(selectedUser => selectedUser.id === user.id)).
			slice(0, 10)
	}, [selectedUsers, data, isAdmin])

	const availableGroups = useMemo(() => {
		if (!groupSelectionEnabled) return []
		return (groupsData?.userGroups ?? [])
			.filter(isDefined)
			.filter(group => group?.lockersystemId === systemId)
			.filter(group => !selectedUsers.some(selectedUser => selectedUser.groupId === group.id))
			.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
	}, [groupSelectionEnabled, groupsData?.userGroups, selectedUsers, systemId])

	useEffect(() => {
		const users = data?.users?.users
		const newUsers = !users ? pendingUsers : pendingUsers.filter(pendingUser => !users.some(user => user.id === pendingUser.id))
		if (newUsers.length) {
			setSelectedUsers(oldUsers => [...oldUsers, ...newUsers])
			setPendingUsers(oldUsers => oldUsers.filter(pendingUser => !newUsers.some(newUser => newUser.id === pendingUser.id)))
		}
	}, [data?.users?.users, pendingUsers])

	const handleAddUser = (user: UserSelection) => {
		if (user.groupId) {
			if (selectedUsers.some(selectedUser => selectedUser.groupId === user.groupId)) {
				setSelectedUsers(selectedUsers.filter(selectedUser => selectedUser.groupId !== user.groupId))
				return
			}
			setSelectedUsers(oldUsers => [...oldUsers, user])
			return
		}

		if (!user.id) {
			setSelectedUsers([user])
			setPendingUsers([])
			return
		}

		setSelectedUsers(oldUsers => oldUsers.filter(existingUser => !existingUser.email))
		if (props.maxSelectedUsers === 1) {
			setPendingUsers([user])
			return
		}
		if (props.maxSelectedUsers && selectedUsers.length >= props.maxSelectedUsers) return
		if (selectedUsers.some(existingUser => existingUser.email === user.email)) {
			setSelectedUsers(selectedUsers.filter(existingUser => existingUser.email !== user.email))
			return
		}
		if (pendingUsers.some(existingUser => existingUser.email === user.email)) {
			setPendingUsers(pendingUsers.filter(existingUser => existingUser.email !== user.email))
			return
		}
		setPendingUsers([...pendingUsers, user])
	}

	const handleIdentityCodeChange = (code: string) => {
		if (!deliveryWithIdentityCode) return
		setIdentityCode(code)
		setSelectedUsers(users => users.filter(user => !user.identityCode))
		setPendingUsers(users => users.filter(user => !user.identityCode))
		if (code.trim()) {
			setSelectedUsers([{
				id: null,
				name: code.trim(),
				email: null,
				identityCode: code.trim()
			}])
		}
	}

	const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
	const emailValid = searchString ? re.test(searchString) : false

	return (
		<WizardStep title={t("")} wizardState={wizardState}>
			<div
				style={{
					fontFamily: "Nunito, Mada-Regular, sans-serif",
					fontSize: 25,
					textAlign: "center",
				}}
			>
				{t("Select receiver")}
			</div>

			<View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", marginVertical: 10 }}>
				<Text text={t("Email Search")} style={{ marginRight: 10 }} />
				{deliveryWithIdentityCode && (
					<>
						<Switch
							value={useIdentityCode}
							onValueChange={setUseIdentityCode}
						/>
						<Text text={t("Identity Code")} style={{ marginLeft: 10 }} />
					</>
				)}
			</View>

			{useIdentityCode && deliveryWithIdentityCode ? (
				<View>
					<Text text={t("Identity Code")} />
					<TextInput
						value={identityCode}
						onChangeText={handleIdentityCodeChange}
						style={{
							marginVertical: 10,
							borderColor: "grey",
							backgroundColor: "white",
							borderWidth: 1,
							height: 40,
							margin: 10,
							padding: 5
						}}
						placeholder={t("Enter identity code")}
					/>
				</View>
			) : (
				<>
					{accessDenied && (
						<Text
							text={t("Lockersystem access denied")}
							style={{ color: "red", textAlign: "center", marginBottom: 10 }}
						/>
					)}
					<SearchBar
						lightTheme
						inputStyle={theme.Input.inputStyle}
						errorStyle={theme.Input.errorMessageStyle}
						labelStyle={theme.Input.labelStyle}
						containerStyle={theme.Input.containerStyle}
						inputContainerStyle={theme.Input.inputContainerStyle}
						searchIcon={undefined}
						clearIcon={undefined}
						cancelIcon={undefined}
						onChangeText={setSearchString}
						value={searchString || ""}
						editable={!accessDenied}
					/>
				</>
			)}
			<MainScrollPanel onRefresh={() => {
				refetch()
				refetchGroups()
			}} refreshing={loading || groupsLoading}>
				{error && <ErrorMessage extra={error} />}
				{groupsError && <ErrorMessage extra={groupsError} />}
				{!useIdentityCode && (props.emailEnabled || props.allowEmailWhenUserNotFound) && !users?.length && emailValid && <View>
					<Base onPress={() => handleAddUser({ email: searchString })}>
						<Text
							text={t("Send to email {{email}}", { email: searchString })}
							style={{ textAlign: "center", color: "#008000" }}
						/>
					</Base>
				</View>}
				{selectedUsers.map(selectedUser => (
					<Base key={selectedUser.groupId || selectedUser.email || selectedUser.id || selectedUser.identityCode} selected={true} onPress={() => {
						if (selectedUser.identityCode) {
							setIdentityCode("")
							setSelectedUsers(selectedUsers.filter(user => !user.identityCode))
						} else if (selectedUser.groupId) {
							setSelectedUsers(selectedUsers.filter(user => user.groupId !== selectedUser.groupId))
						} else {
							setSelectedUsers(selectedUsers.filter(user => user.id !== selectedUser.id))
						}
					}}>
						<Text text={selectedUser.name} bold />
						{selectedUser.groupId && <Text text={t("User group")} />}
						{selectedUser.email && <Text text={selectedUser.email} />}
					</Base>
				))}
				{!useIdentityCode && groupSelectionEnabled && (
					<View>
						<Text text={t("User groups")} style={{ marginVertical: 10, textAlign: "center" }} />
						{availableGroups.map(group => (
							<Base
								key={group.id}
								onPress={() => handleAddUser({
									groupId: group.id,
									name: group.name,
								})}
							>
								<Text text={group.name} bold />
								<Text text={t("{{count}} users", { count: group.users.length })} />
							</Base>
						))}
					</View>
				)}
				{!useIdentityCode && users && users.length > 0 ? (
					users.map(user => {
						if (!user?.email) return null
						const selected = pendingUsers.some(selectedUser => selectedUser.id === user.id)
						return (
							<Base key={user.id} selected={selected} onPress={() => handleAddUser(user)}>
								<Text text={user.name} bold />
								<Text text={user.email} />
							</Base>
						)
					})
				) : null}
			</MainScrollPanel>
			<Panel style={{ flexDirection: "row", justifyContent: "space-between" }}>
				<TouchButton
					type="secondary"
					text={t("Go back")}
					icon={BackIcon}
					onPress={onCancel}
				/>
				<TouchButton
					type="primary"
					disabled={!selectedUsers.length && !pendingUsers.length}
					text={t("Next")}
					onPress={() => onComplete([...selectedUsers, ...pendingUsers])}
				/>
			</Panel>
		</WizardStep>
	)
}
